import { createElement, forwardRef } from "react";

import { useTextOverflowEllipsisDetection } from "@eisox/tools";
import { useComposedRefs } from "@radix-ui/react-compose-refs";

import { HybridTooltip } from "../HybridTooltip";
import { cn } from "../index";

export const TypographyDefaultAsType = "p";
export type TypographyDefaultAsType = typeof TypographyDefaultAsType;

export interface TypographyOwnProps<E extends React.ElementType> {
  children?: React.ReactNode;
  as?: E;
  keepHoverOpen?: boolean;
  className?: string;
  disableTooltipOnTouch?: boolean;
}

export type TypographyProps<E extends React.ElementType> = TypographyOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof TypographyOwnProps<E>>;
export const Typography = forwardRef(
  <E extends React.ElementType = TypographyDefaultAsType>(
    { children, as, keepHoverOpen, className, disableTooltipOnTouch, ...otherProps }: TypographyProps<E>,
    forwardedRef: React.Ref<Element>,
  ) => {
    const Tag = as ?? TypographyDefaultAsType;

    const { isTouch } = HybridTooltip.useHybridTooltipContext("Typography");

    const { ref, isTruncated } = useTextOverflowEllipsisDetection<HTMLElement>();
    const composedRef = useComposedRefs(ref, forwardedRef);

    const element = createElement(
      Tag,
      {
        ref: composedRef,
        className: cn("overflow-hidden text-ellipsis whitespace-nowrap", className),
        ...otherProps,
      },
      children,
    );

    const shouldShowTooltip = isTruncated && !(disableTooltipOnTouch && isTouch);

    return shouldShowTooltip ? (
      <HybridTooltip.Root disableHoverableContent={!keepHoverOpen}>
        <HybridTooltip.Trigger asChild>{element}</HybridTooltip.Trigger>
        <HybridTooltip.Portal>
          <HybridTooltip.Content className="max-w-[400px]">
            {children}
            <HybridTooltip.Arrow />
          </HybridTooltip.Content>
        </HybridTooltip.Portal>
      </HybridTooltip.Root>
    ) : (
      element
    );
  },
) as <E extends React.ElementType = TypographyDefaultAsType>(
  props: TypographyProps<E> & { ref?: React.Ref<Element> },
) => React.ReactElement | null;
